// src/pages/Metrics/components/MetricCard.js

import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Skeleton } from "@mui/material";
import { ArrowDropUp, ArrowDropDown, KeyboardArrowDown } from "@mui/icons-material";
import { useSpring, animated } from "@react-spring/web"; // 🔹 анимации
import FadeIn, { usePrefersReducedMotion } from "../../../components/motion/FadeIn";
import { motionEasing } from "../../../components/motion/easing";
import {
  MOTION,
  CARD_BG_BOTTOM,
  CARD_BG_TOP,
  CARD_BORDER,
  CARD_HIGHLIGHT,
  CARD_LABEL_COLOR,
  CARD_LABEL_SIZE,
  CARD_RADIUS,
  MAIN_CARD_BG_TOP,
  PRESS_SX,
  hexToRgb,
} from "../../../styles/tokens";

const DEFAULT_ACCENT = "#9e9e9e";

const MetricCard = ({
  icon: Icon,
  title,
  value, // строка — для обратной совместимости
  valueNumber, // число — предпочтительный способ для анимации
  format, // (n:number) => string
  suffix = "", // например, " ₽" или " %"
  // Мини-прогрессбар: два сегмента в одной полосе
  progressLeft = null, // 0-100 или null
  progressRight = null, // 0-100 или null
  progressColors = { left: "#7e57c2", right: "#b39ddb" },
  valueStyle = {}, // доп. стили для значения (например, nowrap)
  headerRight = null, // строка справа от заголовка (например, "60% / 40%")
  note = null, // строка мелким текстом под значением
  badgeLeft = null, // { label: string, percent: number, color?: string }
  badgeRight = null, // { label: string, percent: number, color?: string }
  color,
  trend,
  trendLabel = '', // с чем сравнение: «к прошлой пятнице» — мелко под процентом
  large = false,
  loading = false,
  raw = false, // устаревшее: показывать value как есть без анимации
  expandable = false, // можно ли раскрывать карточку
  expandedContent = [], // массив { label: string, value: number } для отображения при раскрытии
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const reducedMotion = usePrefersReducedMotion();

  // Акцент карточки (color — "#rrggbb"): свечение, плашка и иконка
  const accentColor = /^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(color || "") ? color : DEFAULT_ACCENT;
  const accentRgb = hexToRgb(accentColor);
  const iconBoxSize = large ? 42 : 38;
  const iconBoxRadius = large ? "11px" : "10px";
  
  // Палитра цветов для типов оплат (циклическое повторение)
  const paymentTypeColors = [
    "#26c6da", // бирюзовый
    "#ab47bc", // фиолетовый
    "#ff7043", // оранжевый
    "#42a5f5", // синий
    "#66bb6a", // зеленый
    "#ef5350", // красный
    "#ffa726", // янтарный
    "#7e57c2", // фиолетовый
    "#00acc1", // голубой
    "#ec407a", // розовый
  ];
  
  const [trendValue, setTrendValue] = useState(null);
  const [isPositive, setIsPositive] = useState(null);

  // 🔹 Обновляем тренд при изменении пропса
  useEffect(() => {
    const numericTrend = Number(trend);
    if (trend === null || trend === undefined || isNaN(numericTrend)) {
      setTrendValue(null);
      setIsPositive(null);
      return;
    }
    setTrendValue(numericTrend);
    setIsPositive(numericTrend >= 0);
  }, [trend]);

  // 🔹 Число плавно переходит от старого значения к новому за MOTION.data;
  // первое значение после загрузки (и при reduced motion) — сразу, без отсчёта от нуля
  const numericTarget =
    typeof valueNumber === "number"
      ? valueNumber
      : raw
      ? 0
      : Number(value?.toString?.().replace(/\D/g, "")) || 0;

  // Телефон, маленькая карточка: длинное значение («1 234 567 ₽») не помещается в 26 px — шрифт по длине строки
  const shownText = typeof valueNumber === "number"
    ? `${typeof format === "function" ? format(valueNumber) : Math.round(valueNumber).toLocaleString("ru-RU")}${suffix}`
    : String(value ?? "");
  const phoneSize = shownText.length > 11 ? "19px" : shownText.length > 9 ? "22px" : "26px";

  const shownFirstValue = useRef(false);
  const { animatedValue } = useSpring({
    animatedValue: numericTarget,
    immediate: loading || !shownFirstValue.current || reducedMotion,
    config: { duration: MOTION.data, easing: motionEasing },
  });

  useEffect(() => {
    if (!loading) shownFirstValue.current = true;
  }, [loading]);

  // 🔹 Анимация прогресс-баров
  const [prevProgressLeft, setPrevProgressLeft] = useState(progressLeft || 0);
  const [prevProgressRight, setPrevProgressRight] = useState(progressRight || 0);

  useEffect(() => {
    if (typeof progressLeft === "number") setPrevProgressLeft(progressLeft);
    if (typeof progressRight === "number") setPrevProgressRight(progressRight);
  }, [progressLeft, progressRight]);

  const progressLeftAnim = useSpring({
    from: { width: prevProgressLeft },
    to: { width: typeof progressLeft === "number" ? Math.max(0, Math.min(100, progressLeft)) : 0 },
    config: { tension: 120, friction: 20 },
  });

  const progressRightAnim = useSpring({
    from: { width: prevProgressRight },
    to: { width: typeof progressRight === "number" ? Math.max(0, Math.min(100, progressRight)) : 0 },
    config: { tension: 120, friction: 20 },
  });

  // 🔹 Анимация процентов в badge'ах
  const [prevBadgeLeft, setPrevBadgeLeft] = useState(badgeLeft?.percent || 0);
  const [prevBadgeRight, setPrevBadgeRight] = useState(badgeRight?.percent || 0);

  useEffect(() => {
    if (badgeLeft?.percent !== undefined) setPrevBadgeLeft(badgeLeft.percent);
    if (badgeRight?.percent !== undefined) setPrevBadgeRight(badgeRight.percent);
  }, [badgeLeft?.percent, badgeRight?.percent]);

  const badgeLeftAnim = useSpring({
    from: { value: prevBadgeLeft },
    to: { value: badgeLeft?.percent || 0 },
    config: { tension: 120, friction: 20 },
  });

  const badgeRightAnim = useSpring({
    from: { value: prevBadgeRight },
    to: { value: badgeRight?.percent || 0 },
    config: { tension: 120, friction: 20 },
  });

  return (
    // Карточка занимает всю ячейку сетки: соседи в ряду одной высоты, даже если у одной подпись в две строки
    <div style={{ height: "100%" }}>
      <Box
        onClick={expandable ? () => setIsExpanded(!isExpanded) : undefined}
        sx={{
          // Цветное свечение в правом верхнем углу + общий фон карточки из токенов
          background: large
            ? `radial-gradient(420px 180px at 100% 0%, rgba(${accentRgb},0.12), transparent 70%), linear-gradient(180deg, ${MAIN_CARD_BG_TOP}, ${CARD_BG_BOTTOM})`
            : `radial-gradient(220px 140px at 100% 0%, rgba(${accentRgb},0.13), transparent 70%), linear-gradient(180deg, ${CARD_BG_TOP}, ${CARD_BG_BOTTOM})`,
          borderRadius: CARD_RADIUS,
          border: CARD_BORDER,
          boxShadow: `${CARD_HIGHLIGHT}, 0 2px 6px rgba(0,0,0,0.3)`,
          display: "flex",
          flexDirection: "column",
          p: 2,
          // Высота — по ячейке сетки (не меньше 92); у раскрываемой главной карточки ячейка по содержимому,
          // свёрнутые типы оплат схлопываются (maxHeight: 0)
          minHeight: 92,
          height: "100%",
          boxSizing: "border-box",
          transition: "transform 0.3s ease",
          cursor: expandable ? "pointer" : "default",
          ...(expandable && PRESS_SX),
          "&:hover": { transform: expandable ? "translateY(-2px)" : "translateY(-3px)" },
          minWidth: 0,
          width: "100%",
          overflow: "hidden",
        }}
      >
        {/* Подпись и плашка иконки в первой строке, значение — во второй на всю ширину */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto",
            columnGap: 1.5,
            rowGap: 1,
            alignItems: "start",
          }}
        >
        <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
        {title && (
          <Box sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
            <Typography
              sx={{
                fontSize: CARD_LABEL_SIZE,
                color: CARD_LABEL_COLOR,
                lineHeight: 1.4,
              }}
            >
              {title}
            </Typography>
            {headerRight && !expandable && (
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  color: "rgba(255,255,255,0.9)",
                  ml: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {headerRight}
              </Typography>
            )}
          </Box>
        )}

        {badgeLeft && badgeRight && (
          <Box sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.4,
          }}>
            {[badgeLeft, badgeRight].map((b, i) => (
              <Box key={i} sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 0.4,
                py: 0.3,
                borderRadius: 8,
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}>
                <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.85)", whiteSpace: "nowrap" }}>
                  {b.label}
                </Typography>
                <Typography
                  component={animated.span}
                  sx={{ fontSize: "0.78rem", fontWeight: 600, color: b.color || "#fff", whiteSpace: "nowrap" }}
                >
                  {i === 0 ? (
                    <animated.span>
                      {badgeLeftAnim.value.to((val) => `${Math.round(val)}%`)}
                    </animated.span>
                  ) : (
                    <animated.span>
                      {badgeRightAnim.value.to((val) => `${Math.round(val)}%`)}
                    </animated.span>
                  )}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        </Box>

          {/* Плашка с иконкой — справа сверху */}
          {loading ? (
            <Skeleton
              variant="rounded"
              width={iconBoxSize}
              height={iconBoxSize}
              sx={{ borderRadius: iconBoxRadius }}
            />
          ) : (
            Icon && (
              <Box
                sx={{
                  width: iconBoxSize,
                  height: iconBoxSize,
                  borderRadius: iconBoxRadius,
                  backgroundColor: `rgba(${accentRgb},0.15)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ fontSize: large ? 20 : 18, color: accentColor }} />
              </Box>
            )
          )}

          {/* Значение и тренд — вторая строка на всю ширину */}
          <Box sx={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 0.6, minWidth: 0 }}>
            {loading ? (
              <Skeleton
                variant="text"
                width={60}
                height={large ? 40 : 32}
                sx={{
                  borderRadius: "6px",
                }}
              />
            ) : (
              <FadeIn>
                {/* Сумма не сжимается; процент с подписью — рядом, а если не помещается — строкой ниже */}
                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 0.6, rowGap: 0.25, minWidth: 0 }}>
                <Typography
                  component={animated.span}
                  sx={{
                    fontSize: large ? "34px" : { xs: phoneSize, md: "26px" },
                    fontWeight: 800,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.2,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    minWidth: 60,
                    maxWidth: "100%",
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    ...valueStyle,
                  }}
                >
                  {typeof valueNumber === "number" ? (
                    <>
                      <animated.span>
                        {animatedValue.to((val) =>
                          typeof format === "function"
                            ? format(val)
                            : val.toLocaleString("ru-RU", {
                                maximumFractionDigits: 0,
                              })
                        )}
                      </animated.span>
                      {suffix}
                    </>
                  ) : raw ? (
                    <span>{value}</span>
                  ) : (
                    <animated.span>
                      {animatedValue.to((val) =>
                        val.toLocaleString("ru-RU", { maximumFractionDigits: 0 })
                      )}
                    </animated.span>
                  )}
                </Typography>

                {trendValue !== null &&
                  typeof trendValue === "number" &&
                  !isNaN(trendValue) && (
                    // Новое значение тренда — короткий fade (key пересоздаёт блок)
                    <FadeIn key={trendValue} timeout={MOTION.fast}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        flex: "1 1 120px",
                        minWidth: 120,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.2,
                        }}
                      >
                        {isPositive ? (
                          <ArrowDropUp sx={{ color: "limegreen", fontSize: 22 }} />
                        ) : (
                          <ArrowDropDown sx={{ color: "tomato", fontSize: 22 }} />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            color: isPositive ? "limegreen" : "tomato",
                            fontWeight: 600,
                            lineHeight: 1.2,
                          }}
                        >
                          {(isPositive ? "+" : "") +
                            Math.abs(trendValue).toFixed(1) +
                            "%"}
                        </Typography>
                      </Box>
                      {trendLabel && (
                        <Typography sx={{ fontSize: "11px", lineHeight: 1.25, color: "#8e8e93", ml: 0.5 }}>
                          {trendLabel}
                        </Typography>
                      )}
                    </Box>
                    </FadeIn>
                  )}
                </Box>
              </FadeIn>
            )}
          </Box>
        </Box>

        {/* Необязательная поясняющая строка под значением (например, «из них 26 в открытых») */}
        {note && !loading && (
          <Typography sx={{ mt: 0.25, fontSize: "12px", color: "#8e8e93", lineHeight: 1.3 }}>{note}</Typography>
        )}

        {/* Optional split progress bar - отображается внизу карточки */}
        {typeof progressLeft === "number" && typeof progressRight === "number" && (
          <Box sx={{ mt: 0.5, width: "100%" }}>
            <Box
              sx={{
                height: 5,
                width: "100%",
                borderRadius: 4,
                overflow: "hidden",
                display: "flex",
                background: "rgba(255,255,255,0.12)",
                position: "relative",
                transition: "height 0.3s ease",
                "&:hover": {
                  height: 6,
                },
              }}
            >
              <animated.div
                style={{
                  width: progressLeftAnim.width.to((w) => `${w}%`),
                  height: "100%",
                  background: progressColors.left,
                  transition: "background 0.3s ease",
                  boxShadow: `0 0 8px ${progressColors.left}40`,
                  borderRadius: typeof progressRight === "number" && progressRight > 0 ? "4px 0 0 4px" : "4px",
                }}
              />
              <animated.div
                style={{
                  width: progressRightAnim.width.to((w) => `${w}%`),
                  height: "100%",
                  background: progressColors.right,
                  transition: "background 0.3s ease",
                  boxShadow: `0 0 8px ${progressColors.right}40`,
                  borderRadius: typeof progressLeft === "number" && progressLeft > 0 ? "0 4px 4px 0" : "4px",
                }}
              />
            </Box>
          </Box>
        )}

        {/* Строка "Все типы оплат" внизу карточки */}
        {expandable && expandedContent.length > 0 && (
          <Box
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 1.5,
              pt: 1.5,
              borderTop: "1px solid rgba(255,255,255,0.1)",
              cursor: "pointer",
              "&:hover": {
                opacity: 0.8,
              },
            }}
          >
            <Typography
              sx={{
                fontSize: "0.85rem",
                color: "rgba(255,255,255,0.9)",
                fontWeight: 500,
              }}
            >
              Все типы оплат
            </Typography>
            <KeyboardArrowDown
              sx={{
                fontSize: 18,
                color: "rgba(255,255,255,0.9)",
                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
              }}
            />
          </Box>
        )}

        {/* Раскрывающийся контент с типами оплат */}
        {expandable && expandedContent.length > 0 && (
          <Box
            sx={{
              maxHeight: isExpanded ? "1000px" : "0px",
              opacity: isExpanded ? 1 : 0,
              overflow: "hidden",
              transition: "max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-in-out",
              mt: isExpanded ? 1.5 : 0,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
              {expandedContent.map((item, index) => {
                // Используем переданный цвет из expandedContent, если есть, иначе из палитры
                const itemColor = item.color || paymentTypeColors[index % paymentTypeColors.length];
                
                return (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.4,
                      px: 1,
                      py: 0.8,
                      borderRadius: 1,
                      background: `${itemColor}15`, // 15 = ~8% прозрачности
                      border: `1px solid ${itemColor}40`, // 40 = ~25% прозрачности
                      borderLeft: `4px solid ${itemColor}`, // Цветная полоска слева
                      position: "relative",
                      opacity: isExpanded ? 1 : 0,
                      transform: isExpanded ? "translateY(0)" : "translateY(-10px)",
                      transition: `opacity 0.3s ease ${index * 0.03}s, transform 0.3s ease ${index * 0.03}s, all 0.2s ease`,
                      "&:hover": {
                        background: `${itemColor}25`, // При наведении чуть ярче
                        transform: isExpanded ? "translateX(2px)" : "translateY(-10px)",
                      },
                    }}
                  >
                    {/* Первая строка: Тип оплаты: Сумма */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color: "rgba(255,255,255,0.9)",
                          fontWeight: 500,
                        }}
                      >
                        {item.label}:
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.9rem",
                          fontWeight: 600,
                          color: "#fff",
                        }}
                      >
                        {Number(item.value).toLocaleString("ru-RU", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })} ₽
                      </Typography>
                    </Box>
                    
                    {/* Вторая строка: Заказов: Кол-во */}
                    {item.ordersCount !== undefined && item.ordersCount > 0 && (
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            color: "rgba(255,255,255,0.6)",
                            fontWeight: 400,
                          }}
                        >
                          Заказов:
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            color: "rgba(255,255,255,0.8)",
                            fontWeight: 500,
                          }}
                        >
                          {item.ordersCount} {item.ordersCount === 1 ? "заказ" : item.ordersCount < 5 ? "заказа" : "заказов"}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
        )}
      </Box>
    </div>
  );
};

export default MetricCard;